import { environment } from '../../environments/environment';

/** UTF-8 BOM helps Windows open saved HTML with correct encoding. */
const UTF8_BOM = '\uFEFF';

/** Same Vazirmatn stack as Admin (`styles.css`). */
export const PRINT_DOCUMENT_FONT_STACK = '"Vazirmatn", "Segoe UI", Tahoma, sans-serif';

const FONT_CSS_PATH = '/fonts/vazirmatn/vazirmatn.css';

export function resolvePrintFontCssUrl(): string {
  const base = (environment.apiBaseUrl ?? '').trim().replace(/\/+$/, '');
  return base ? `${base}${FONT_CSS_PATH}` : FONT_CSS_PATH;
}

function buildPrintFontHeadMarkup(fontCssUrl: string): string {
  const style = `<style>html, body, body *, table, th, td { font-family: ${PRINT_DOCUMENT_FONT_STACK}; }</style>`;
  return `<link rel="stylesheet" href="${fontCssUrl}" />\n${style}`;
}

function injectPrintDocumentFonts(html: string, fontCssUrl: string): string {
  if (html.includes(FONT_CSS_PATH)) {
    return html;
  }

  const fontHead = buildPrintFontHeadMarkup(fontCssUrl);
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${fontHead}</head>`);
  }

  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, match => `${match}${fontHead}`);
  }

  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, match => `${match}<head><meta charset="utf-8" />${fontHead}</head>`);
  }

  return html;
}

/**
 * Ensures printable HTML is a full document with charset (needed for file:// and blob URLs).
 */
export function preparePrintHtml(html: string | null | undefined): string {
  const trimmed = (html ?? '').trim();
  if (!trimmed) {
    return '';
  }

  const fontCssUrl = resolvePrintFontCssUrl();
  let documentHtml = trimmed;

  if (/<html[\s>]/i.test(documentHtml)) {
    if (!/<meta[^>]+charset\s*=/i.test(documentHtml)) {
      documentHtml = documentHtml.replace(/<head(\s[^>]*)?>/i, match => `${match}\n  <meta charset="utf-8" />`);
    }
  } else {
    documentHtml = `<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>Print</title>
  <style>
    body { margin: 0; padding: 12mm 10mm; font-size: 11px; line-height: 1.45; color: #000; }
  </style>
</head>
<body>
${documentHtml}
</body>
</html>`;
  }

  return injectPrintDocumentFonts(documentHtml, fontCssUrl);
}

export function createPrintHtmlBlob(html: string | null | undefined): Blob | null {
  const prepared = preparePrintHtml(html);
  if (!prepared) {
    return null;
  }

  return new Blob([UTF8_BOM, prepared], { type: 'text/html;charset=utf-8' });
}

/**
 * Opens HTML in a new tab and triggers print. Uses a blob URL (works with pop-up blockers better than document.write + noopener).
 */
export function openHtmlDocumentForPrint(html: string | null | undefined): boolean {
  const blob = createPrintHtmlBlob(html);
  if (!blob) {
    return false;
  }

  const url = URL.createObjectURL(blob);
  const popup = window.open(url, '_blank');
  if (!popup) {
    URL.revokeObjectURL(url);
    return false;
  }

  let printed = false;
  const printOnce = (): void => {
    if (printed || popup.closed) {
      return;
    }

    printed = true;
    try {
      popup.focus();
      popup.print();
    } catch {
      /* ignore — user can print from the tab */
    }
  };

  popup.addEventListener('load', () => printOnce(), { once: true });
  window.setTimeout(() => printOnce(), 800);
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);

  return true;
}

export function downloadHtmlDocument(html: string | null | undefined, fileName: string): boolean {
  const blob = createPrintHtmlBlob(html);
  if (!blob) {
    return false;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName || 'document.html';
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}
